import { useMemo, useState } from "react";
import { ChevronDown, ChevronUp, Mail, Pencil, Plus, Trash2, UserRound, X } from "lucide-react";
import { normalizeErrorMessage } from "../utils/errors";
import { getAvatarColor } from "../utils/tasks";
import { InlineIcon, UiIcon } from "./Icon";

import 'react-phone-number-input/style.css'
import PhoneInput from 'react-phone-number-input'
import phoneInputLabels from 'react-phone-number-input/locale/pl'

const TASK_STATUS_META = {
  new: {
    label: "Nowe",
    textColor: "var(--color-primary)",
    bgColor: "var(--badge-new-bg)",
    borderColor: "var(--badge-new-border)",
  },
  in_progress: {
    label: "W toku",
    textColor: "var(--badge-progress-text)",
    bgColor: "var(--badge-progress-bg)",
    borderColor: "color-mix(in srgb, var(--badge-progress-text) 30%, white)",
  },
  problem: {
    label: "Problem",
    textColor: "var(--badge-problem-text)",
    bgColor: "var(--badge-problem-bg)",
    borderColor: "color-mix(in srgb, var(--badge-problem-text) 30%, white)",
  },
  done: {
    label: "Done",
    textColor: "var(--badge-done-text)",
    bgColor: "var(--badge-done-bg)",
    borderColor: "color-mix(in srgb, var(--badge-done-text) 30%, white)",
  },
};

function ContractorAvatar({ contractor, size = 36 }) {
  const color = getAvatarColor(contractor?.id ?? -1);
  const initials = contractor?.name
    ? contractor.name
        .split(" ")
        .slice(0, 2)
        .map((w) => w[0])
        .join("")
        .toUpperCase()
    : "?";

  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        background: `${color}25`,
        border: `1px solid ${color}66`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      <span style={{ color, fontSize: size * 0.38, fontWeight: "var(--font-semibold)" }}>{initials}</span>
    </div>
  );
}

export default function ContractorsScreen({
  auth,
  userTier,
  eventName,
  contractors,
  tasks,
  userLimits,
  onClose,
  onRefresh,
  onCreateContractor,
  onUpdateContractor,
  onDeleteContractor,
  onCreateTaskForContractor,
  onOpenTask,
  isModal,
}) {
  const [expandedByContractor, setExpandedByContractor] = useState({});
  const [doneExpandedByContractor, setDoneExpandedByContractor] = useState({});
  const [modalVisible, setModalVisible] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [position, setPosition] = useState("");
  const [company, setCompany] = useState("");
  const [editingContractorId, setEditingContractorId] = useState(null);

  const maxContractors = userLimits?.contractors_per_event?.max ?? null;
  const contractorsAtLimit = maxContractors !== null && contractors.length >= maxContractors;

  const sortedContractors = useMemo(() => {
    return [...contractors].sort((a, b) => a.name.localeCompare(b.name, "pl"));
  }, [contractors]);

  const tasksByContractor = useMemo(() => {
    const grouped = {};
    sortedContractors.forEach((contractor) => {
      grouped[contractor.id] = [];
    });

    tasks.forEach((task) => {
      const contractorId = task?.contact?.id;
      if (contractorId && grouped[contractorId]) {
        grouped[contractorId].push(task);
      }
    });

    Object.keys(grouped).forEach((id) => {
      grouped[id].sort((a, b) => {
        const aDate = `${a.date || "9999-12-31"} ${a.time || "23:59"}`;
        const bDate = `${b.date || "9999-12-31"} ${b.time || "23:59"}`;
        return aDate.localeCompare(bDate);
      });
    });

    return grouped;
  }, [sortedContractors, tasks]);

  const resetForm = () => {
    setName("");
    setEmail("");
    setPhone("");
    setPosition("");
    setCompany("");
    setFormError("");
  };

  const closeModal = () => {
    if (saving) return;
    setModalVisible(false);
    resetForm();
  };

  const openCreateModal = () => {
    setEditingContractorId(null);
    resetForm();
    setModalVisible(true);
  };

  const openEditModal = (contractor) => {
    setEditingContractorId(contractor.id);
    setName(contractor.name || "");
    setEmail(contractor.email || "");
    setPhone(contractor.phone || "");
    setPosition(contractor.position || "");
    setCompany(contractor.company || "");
    setFormError("");
    setModalVisible(true);
  };

  const toggleExpanded = (contractorId) => {
    setExpandedByContractor((prev) => ({
      ...prev,
      [contractorId]: !prev[contractorId],
    }));
  };

  const toggleDoneExpanded = (contractorId) => {
    setDoneExpandedByContractor((prev) => ({
      ...prev,
      [contractorId]: !prev[contractorId],
    }));
  };

  const handleSaveContractor = async () => {
    setFormError("");

    if (!name.trim()) {
      setFormError("Imię i nazwisko wykonawcy jest wymagane.");
      return;
    }

    setSaving(true);

    const result = editingContractorId
      ? await onUpdateContractor(editingContractorId, {
          name: name.trim(),
          email: email.trim() || null,
          phone: phone || null,
          position: position.trim() || null,
          company: company.trim() || null,
        })
      : await onCreateContractor({
          name: name.trim(),
          email: email.trim() || null,
          phone,
          position: position.trim() || null,
          company: company.trim() || null,
        });

    setSaving(false);

    if (!result?.ok) {
      if (result.error && result.error.includes("Incorrect phone number format")) {
        setFormError("Wprowadź poprawny numer telefonu (np. +1234567890).");
        return;
      }
      setFormError(normalizeErrorMessage(result?.error) || "Nie udało się zapisać wykonawcy.");
      return;
    }

    closeModal();
  };

  const handleDelete = async (contractor) => {
    const confirmed = window.confirm(`Czy na pewno usunąć ${contractor.name}?`);
    if (!confirmed) return;

    const result = await onDeleteContractor(contractor.id);
    if (!result?.ok) {
      window.alert(normalizeErrorMessage(result?.error) || "Nie udało się usunąć wykonawcy.");
    }
  };

  const renderTaskRow = (task) => {
    const meta = TASK_STATUS_META[task.status] || {
      label: task.status || "Nieznany",
      textColor: "var(--color-text-muted)",
      bgColor: "var(--color-bg-subtle)",
      borderColor: "var(--color-border)",
    };

    return (
      <button
        key={task.id}
        onClick={() => onOpenTask(task)}
        style={{
          width: "100%",
          textAlign: "left",
          border: "none",
          borderBottom: "1px solid var(--color-border)",
          background: "var(--color-bg-app)",
          padding: "10px 12px",
          cursor: "pointer",
          fontFamily: "inherit",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div
            style={{
              flex: 1,
              fontSize: "var(--text-sm)",
              fontWeight: "var(--font-medium)",
              color: "var(--color-text-primary)",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {task.desc || task.name || `Zadanie #${task.id}`}
          </div>
          <span
            style={{
              borderRadius: "var(--radius-full)",
              border: `1px solid ${meta.borderColor}`,
              background: meta.bgColor,
              color: meta.textColor,
              fontSize: "var(--text-xs)",
              fontWeight: "var(--font-semibold)",
              padding: "3px 8px",
              lineHeight: 1.2,
            }}
          >
            {meta.label}
          </span>
        </div>
        <div style={{ marginTop: 2, fontSize: "var(--text-xs)", color: "var(--color-text-muted)" }}>
          {task.date || "Bez daty"}{task.time ? ` • ${task.time}` : ""}
        </div>
      </button>
    );
  };

  return (
    <div
      style={{
        fontFamily: "var(--font-body)",
        background: "var(--color-bg-app)",
        minHeight: "100dvh",
        maxWidth: 480,
        width: "calc(100% - 16px)",
        margin: "0 auto",
      }}
    >
      <div
        style={{
          background: "var(--color-bg-card)",
          borderBottom: "1px solid var(--color-border)",
          padding: "16px 20px",
          paddingTop: "calc(16px + env(safe-area-inset-top))",
          display: "flex",
          alignItems: "center",
          gap: 10,
        }}
      >
        <div style={{ flex: 1 }}>
          <div
            style={{
              fontFamily: "var(--font-display)",
              fontSize: "var(--text-h3)",
              color: "var(--color-text-primary)",
              fontWeight: "var(--font-semibold)",
            }}
          >
            Wykonawcy
          </div>
          {eventName && (
            <div style={{ marginTop: 2, fontSize: "var(--text-xs)", color: "var(--color-text-muted)" }}>{eventName}</div>
          )}
        </div>

        <button
          onClick={openCreateModal}
          disabled={contractorsAtLimit}
          style={{
            border: contractorsAtLimit ? "1px solid var(--color-border)" : "none",
            background: contractorsAtLimit ? "var(--color-bg-subtle)" : "var(--color-primary)",
            color: contractorsAtLimit ? "var(--color-text-muted)" : "var(--color-bg-card)",
            borderRadius: "var(--radius-md)",
            padding: "8px 10px",
            cursor: contractorsAtLimit ? "not-allowed" : "pointer",
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            fontSize: "var(--text-sm)",
            fontWeight: "var(--font-semibold)",
          }}
        >
          <InlineIcon Icon={Plus} /> Dodaj
        </button>
      </div>

      <div style={{ padding: 16, display: "flex", flexDirection: "column", gap: 10 }}>
        <div style={{ display: "flex", gap: 10 }}>
          <button
            onClick={onRefresh}
            style={{
              flex: 1,
              border: "1px solid var(--color-primary-border)",
              background: "var(--color-primary)",
              color: "var(--color-bg-card)",
              borderRadius: "var(--radius-md)",
              padding: "10px 12px",
              fontSize: "var(--text-sm)",
              fontWeight: "var(--font-medium)",
              cursor: "pointer",
              fontFamily: "inherit",
            }}
          >
            Odśwież
          </button>
        </div>

        {contractorsAtLimit && (
          <div
            style={{
              borderRadius: "var(--radius-md)",
              border: "1px solid color-mix(in srgb, var(--color-warning) 35%, white)",
              background: "var(--color-warning-bg)",
              padding: 10,
              color: "var(--color-warning)",
              fontSize: "var(--text-sm)",
            }}
          >
            Osiągnięto limit {maxContractors} wykonawców dla tego planu.
          </div>
        )}

        {sortedContractors.length === 0 ? (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
              border: "1px solid var(--color-border)",
              borderRadius: "var(--radius-md)",
              padding: 14,
              background: "var(--color-bg-card)",
              color: "var(--color-text-muted)",
              fontSize: "var(--text-sm)",
            }}
          >
            <InlineIcon Icon={UserRound} /> Brak wykonawców dla tego wydarzenia.
          </div>
        ) : (
          sortedContractors.map((contractor) => {
            const contractorTasks = tasksByContractor[contractor.id] || [];
            const activeTasks = contractorTasks.filter((task) => task.status !== "done");
            const doneTasks = contractorTasks.filter((task) => task.status === "done");
            const isDoneExpanded = Boolean(doneExpandedByContractor[contractor.id]);

            return (
              <div
                key={contractor.id}
                style={{
                  background: "var(--color-bg-card)",
                  borderRadius: "var(--radius-md)",
                  border: "1px solid var(--color-border)",
                  padding: 12,
                }}
              >
                <button
                  onClick={() => toggleExpanded(contractor.id)}
                  style={{
                    width: "100%",
                    border: "none",
                    background: "none",
                    padding: 0,
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    textAlign: "left",
                    cursor: "pointer",
                    fontFamily: "inherit",
                  }}
                >
                  <ContractorAvatar contractor={contractor} />

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: "var(--text-body)",
                        color: "var(--color-text-primary)",
                        fontWeight: "var(--font-semibold)",
                      }}
                    >
                      {contractor.name}
                    </div>
                    {contractor.role && (
                      <div
                        style={{
                          marginTop: 2,
                          fontSize: "var(--text-sm)",
                          color: "var(--color-text-muted)",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {contractor.role}
                      </div>
                    )}
                    {contractor.phone && (
                      <div
                        style={{
                          marginTop: 2,
                          fontSize: "var(--text-sm)",
                          color: "var(--color-text-muted)",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {contractor.phone}
                      </div>
                    )}
                    {contractor.email && (
                      <div
                        style={{
                          marginTop: 2,
                          fontSize: "var(--text-sm)",
                          color: "var(--color-text-muted)",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          display: "flex",
                          alignItems: "center",
                          gap: 4,
                        }}
                      >
                        <InlineIcon Icon={Mail} size={12} />{contractor.email}
                      </div>
                    )}
                  </div>

                  <span
                    style={{
                      borderRadius: "var(--radius-full)",
                      background: "var(--color-bg-subtle)",
                      color: "var(--color-text-muted)",
                      fontSize: "var(--text-xs)",
                      fontWeight: "var(--font-semibold)",
                      padding: "3px 8px",
                      lineHeight: 1.2,
                    }}
                  >
                    {activeTasks.length}
                  </span>

                  <span style={{ color: "var(--color-text-muted)", display: "inline-flex", alignItems: "center" }}>
                    {expandedByContractor[contractor.id] ? <UiIcon Icon={ChevronUp} /> : <UiIcon Icon={ChevronDown} />}
                  </span>
                </button>

                <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
                  <button
                    onClick={() => openEditModal(contractor)}
                    style={{
                      borderRadius: "var(--radius-full)",
                      border: "1px solid var(--color-primary-border)",
                      background: "var(--color-primary-light)",
                      color: "var(--color-primary)",
                      padding: "5px 10px",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 4,
                      cursor: "pointer",
                      fontSize: "var(--text-xs)",
                      fontWeight: "var(--font-semibold)",
                    }}
                  >
                    <InlineIcon Icon={Pencil} /> Edytuj
                  </button>

                  <button
                    onClick={() => handleDelete(contractor)}
                    style={{
                      borderRadius: "var(--radius-full)",
                      border: "1px solid color-mix(in srgb, var(--color-danger) 35%, white)",
                      background: "var(--color-danger-bg)",
                      color: "var(--color-danger)",
                      padding: "5px 10px",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 4,
                      cursor: "pointer",
                      fontSize: "var(--text-xs)",
                      fontWeight: "var(--font-semibold)",
                    }}
                  >
                    <InlineIcon Icon={Trash2} /> Usuń
                  </button>

                  <button
                    onClick={() => onCreateTaskForContractor(contractor)}
                    style={{
                      borderRadius: "var(--radius-full)",
                      border: "1px solid var(--color-primary-border)",
                      background: "var(--color-primary-light)",
                      color: "var(--color-primary)",
                      padding: "5px 10px",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 4,
                      cursor: "pointer",
                      fontSize: "var(--text-xs)",
                      fontWeight: "var(--font-semibold)",
                    }}
                  >
                    <InlineIcon Icon={Plus} /> Nowe zadanie
                  </button>
                </div>

                {expandedByContractor[contractor.id] && (
                  <div
                    style={{
                      marginTop: 10,
                      borderRadius: "var(--radius-md)",
                      border: "1px solid var(--color-border)",
                      overflow: "hidden",
                    }}
                  >
                    {contractorTasks.length === 0 ? (
                      <div style={{ padding: "10px 12px", fontSize: "var(--text-xs)", color: "var(--color-text-muted)" }}>
                        Brak zadań przypisanych do tego wykonawcy.
                      </div>
                    ) : (
                      <>
                        {activeTasks.map(renderTaskRow)}

                        {doneTasks.length > 0 && (
                          <>
                            <button
                              onClick={() => toggleDoneExpanded(contractor.id)}
                              style={{
                                width: "100%",
                                border: "none",
                                borderTop: "1px solid var(--color-border)",
                                background: "var(--color-bg-subtle)",
                                padding: "10px 12px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                cursor: "pointer",
                                fontFamily: "inherit",
                              }}
                            >
                              <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                                <span
                                  style={{
                                    fontSize: "var(--text-sm)",
                                    color: "var(--color-text-primary)",
                                    fontWeight: "var(--font-semibold)",
                                  }}
                                >
                                  Done
                                </span>
                                <span
                                  style={{
                                    borderRadius: "var(--radius-full)",
                                    background: "var(--badge-done-bg)",
                                    border: "1px solid var(--badge-done-border)",
                                    color: "var(--badge-done-text)",
                                    fontSize: "var(--text-xs)",
                                    fontWeight: "var(--font-semibold)",
                                    padding: "2px 7px",
                                  }}
                                >
                                  {doneTasks.length}
                                </span>
                              </span>
                              <span style={{ color: "var(--color-text-muted)", display: "inline-flex", alignItems: "center" }}>
                                {isDoneExpanded ? <UiIcon Icon={ChevronUp} /> : <UiIcon Icon={ChevronDown} />}
                              </span>
                            </button>
                            {isDoneExpanded && doneTasks.map(renderTaskRow)}
                          </>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {modalVisible && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(15,23,42,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1200,
            padding: 16,
            boxSizing: "border-box",
          }}
          onClick={closeModal}
        >
          <div
            onClick={(event) => event.stopPropagation()}
            style={{
              width: "100%",
              maxWidth: 420,
              borderRadius: "var(--radius-lg)",
              border: "1px solid var(--color-border)",
              background: "var(--color-bg-card)",
              padding: 16,
              boxSizing: "border-box",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <div
                style={{
                  fontSize: "var(--text-body)",
                  color: "var(--color-text-primary)",
                  fontWeight: "var(--font-semibold)",
                }}
              >
                {editingContractorId ? "Edytuj wykonawcę" : "Nowy wykonawca"}
              </div>
              <button
                onClick={closeModal}
                style={{
                  border: "none",
                  background: "none",
                  color: "var(--color-text-muted)",
                  cursor: "pointer",
                  padding: 0,
                  display: "inline-flex",
                  alignItems: "center",
                }}
              >
                <UiIcon Icon={X} />
              </button>
            </div>

            {formError && (
              <div
                style={{
                  marginBottom: 12,
                  borderRadius: "var(--radius-md)",
                  border: "1px solid color-mix(in srgb, var(--color-danger) 35%, white)",
                  background: "var(--color-danger-bg)",
                  color: "var(--color-danger)",
                  fontSize: "var(--text-sm)",
                  padding: 10,
                }}
              >
                {formError}
              </div>
            )}

            <label style={{ display: "block", marginBottom: 6, fontSize: "var(--text-sm)", color: "var(--color-text-primary)", fontWeight: "var(--font-medium)" }}>
              Imię i nazwisko *
            </label>
            <input
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="Jan Kowalski"
              style={{
                width: "100%",
                boxSizing: "border-box",
                border: "1px solid var(--color-border)",
                borderRadius: "var(--radius-md)",
                padding: "11px 12px",
                fontSize: "var(--text-body)",
                marginBottom: 12,
                minHeight: 44,
              }}
            />

            <label style={{ display: "block", marginBottom: 6, fontSize: "var(--text-sm)", color: "var(--color-text-primary)", fontWeight: "var(--font-medium)" }}>
              E-mail
            </label>
            <input
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="jan@example.com"
              style={{
                width: "100%",
                boxSizing: "border-box",
                border: "1px solid var(--color-border)",
                borderRadius: "var(--radius-md)",
                padding: "11px 12px",
                fontSize: "var(--text-body)",
                marginBottom: 12,
                minHeight: 44,
              }}
            />

            <label style={{ display: "block", marginBottom: 6, fontSize: "var(--text-sm)", color: "var(--color-text-primary)", fontWeight: "var(--font-medium)" }}>
              Telefon
            </label>
            <PhoneInput
              defaultCountry="PL"
              international
              displayInitialValueAsLocal
              countryCallingCodeEditable={false}
              value={phone || undefined}
              onChange={(value) => setPhone(value || "")}
              labels={phoneInputLabels}
              numberInputProps={{
                placeholder: "Telefon",
                style: {
                  border: "none",
                  outline: "none",
                  width: "100%",
                  minWidth: 0,
                  background: "transparent",
                },
              }}
              style={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                border: "1px solid var(--color-border)",
                borderRadius: "var(--radius-md)",
                padding: "9px 10px",
                fontSize: "var(--text-body)",
                marginBottom: 12,
                minHeight: 44,
                boxSizing: "border-box",
              }}
            />

            <label style={{ display: "block", marginBottom: 6, fontSize: "var(--text-sm)", color: "var(--color-text-primary)", fontWeight: "var(--font-medium)" }}>
              Stanowisko
            </label>
            <input
              value={position}
              onChange={(event) => setPosition(event.target.value)}
              placeholder="Fotograf"
              style={{
                width: "100%",
                boxSizing: "border-box",
                border: "1px solid var(--color-border)",
                borderRadius: "var(--radius-md)",
                padding: "11px 12px",
                fontSize: "var(--text-body)",
                marginBottom: 12,
                minHeight: 44,
              }}
            />

            <label style={{ display: "block", marginBottom: 6, fontSize: "var(--text-sm)", color: "var(--color-text-primary)", fontWeight: "var(--font-medium)" }}>
              Firma
            </label>
            <input
              value={company}
              onChange={(event) => setCompany(event.target.value)}
              placeholder="Studio XYZ"
              style={{
                width: "100%",
                boxSizing: "border-box",
                border: "1px solid var(--color-border)",
                borderRadius: "var(--radius-md)",
                padding: "11px 12px",
                fontSize: "var(--text-body)",
                marginBottom: 16,
                minHeight: 44,
              }}
            />

            <button
              onClick={handleSaveContractor}
              disabled={saving}
              style={{
                width: "100%",
                border: "none",
                background: "var(--color-primary)",
                color: "var(--color-bg-card)",
                borderRadius: "var(--radius-md)",
                padding: "12px",
                fontSize: "var(--text-body)",
                fontWeight: "var(--font-semibold)",
                cursor: saving ? "default" : "pointer",
                fontFamily: "inherit",
                opacity: saving ? 0.75 : 1,
              }}
            >
              {saving ? (editingContractorId ? "Zapisywanie..." : "Dodawanie...") : (editingContractorId ? "Zapisz zmiany" : "Dodaj wykonawcę")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
